import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@mui/material";

type DashboardSectionMotionValue = {
  animationKey: string;
  activeSectionId: string | null;
  activateSection: (sectionId: string) => void;
};

const DashboardSectionMotionContext = createContext<DashboardSectionMotionValue>({
  animationKey: "init",
  activeSectionId: null,
  activateSection: () => undefined,
});

/** Once a section has played load animation for a filter key, skip skeleton→content replays. */
let activeMotionKey = "";
const playedSectionIds = new Set<string>();

function syncMotionKey(animationKey: string) {
  if (animationKey !== activeMotionKey) {
    activeMotionKey = animationKey;
    playedSectionIds.clear();
  }
}

export const DashboardSectionMotionProvider: React.FC<{
  animationKey: string;
  children: React.ReactNode;
}> = ({ animationKey, children }) => {
  syncMotionKey(animationKey);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  useEffect(() => {
    setActiveSectionId(null);
  }, [animationKey]);

  const activateSection = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
  }, []);

  const value = useMemo(
    () => ({
      animationKey,
      activeSectionId,
      activateSection,
    }),
    [animationKey, activeSectionId, activateSection]
  );

  return (
    <DashboardSectionMotionContext.Provider value={value}>
      {children}
    </DashboardSectionMotionContext.Provider>
  );
};

export const useActivateDashboardSection = () => {
  const { activateSection, activeSectionId } = useContext(
    DashboardSectionMotionContext
  );
  return { activateSection, activeSectionId };
};

interface DashboardSectionDividerProps {
  /** Stable id so skeleton and loaded content share one animation per filter change */
  sectionId?: string;
  delayMs?: number;
}

/**
 * On load / filter: orange sweeps the full line, then settles to a short accent.
 * On section click: orange covers the full line; leaving for another section retracts it.
 */
export const DashboardSectionDivider: React.FC<DashboardSectionDividerProps> = ({
  sectionId = "section",
  delayMs = 0,
}) => {
  const { animationKey, activeSectionId } = useContext(
    DashboardSectionMotionContext
  );
  syncMotionKey(animationKey);

  const playToken = `${animationKey}::${sectionId}`;
  const shouldPlayLoad = !playedSectionIds.has(playToken);
  if (shouldPlayLoad) {
    playedSectionIds.add(playToken);
  }

  const [completedToken, setCompletedToken] = useState<string | null>(
    shouldPlayLoad ? null : playToken
  );
  const loadDone = completedToken === playToken;
  const isActive = activeSectionId === sectionId;

  useEffect(() => {
    if (!shouldPlayLoad) {
      setCompletedToken(playToken);
      return;
    }
    setCompletedToken(null);
    const timer = window.setTimeout(() => setCompletedToken(playToken), 920 + delayMs);
    return () => window.clearTimeout(timer);
  }, [playToken, shouldPlayLoad, delayMs]);

  const showLoadAnimation = shouldPlayLoad && !loadDone;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 2.5,
        mb: 1.5,
        "@keyframes dashboardDividerFade": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        "@keyframes dashboardDividerSweep": {
          "0%": { width: 0, opacity: 0.9 },
          "42%": { width: "100%", opacity: 1 },
          "100%": { width: 28, opacity: 1 },
        },
        ...(showLoadAnimation
          ? {
              animation: "dashboardDividerFade 320ms ease-out both",
              animationDelay: `${delayMs}ms`,
            }
          : { opacity: 1 }),
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          "& > *": {
            animation: "none !important",
            width: isActive ? "100% !important" : "28px !important",
            opacity: "1 !important",
            transition: "none !important",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: "1px",
          transform: "translateY(-50%)",
          background:
            "linear-gradient(90deg, #e5e7eb 0%, #e5e7eb 70%, rgba(229, 231, 235, 0) 100%)",
        }}
      />
      <Box
        key={showLoadAnimation ? playToken : `idle::${sectionId}`}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: 2.5,
          width: showLoadAnimation ? 28 : isActive ? "100%" : 28,
          borderRadius: 999,
          backgroundColor: "#d2363a",
          transformOrigin: "left center",
          ...(showLoadAnimation
            ? {
                animation:
                  "dashboardDividerSweep 900ms cubic-bezier(0.45, 0, 0.2, 1) both",
                animationDelay: `${delayMs}ms`,
              }
            : {
                transition:
                  "width 480ms cubic-bezier(0.45, 0, 0.2, 1), opacity 280ms ease",
              }),
        }}
      />
    </Box>
  );
};
